import { useAtom } from "jotai";
import { atomFamily, atomWithStorage, useAtomValue } from "jotai/utils";

import { DataFilter } from "interfaces";

import * as flowConfig from "flows/impact-analysis-binary/config";

const PREFIX = `${flowConfig.FLOW_NAME}/create`;

const overlayToBeShown = atomWithStorage(`${PREFIX}/show-create-overlay`, true);

export const useOverlayStatus = () => useAtom(overlayToBeShown);

export const useGetOverlayDisplay = () => useAtomValue(overlayToBeShown);

const inputFile = atomWithStorage<string>(`${PREFIX}/input-file`, null);

export const useInputFile = () => useAtom(inputFile);

export const useGetInputFile = () => useAtomValue(inputFile);

const yDefinition = atomFamily((fileId: string) =>
  atomWithStorage<DataFilter.Filter[]>(`${PREFIX}/${fileId}/y-definition`, [])
);

export const useYDefinition = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(yDefinition(fileId));
};

export const useGetYDefinition = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(yDefinition(fileId));
};

const yOnesTag = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/ones-tag`, "success")
);

export const useYOnesTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(yOnesTag(fileId));
};

export const useGetYOnesTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(yOnesTag(fileId));
};

const yZerosTag = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/zeros-tag`, "failure")
);

export const useYZerosTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(yZerosTag(fileId));
};

export const useGetYZerosTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(yZerosTag(fileId));
};

const treatmentDefinition = atomFamily((fileId: string) =>
  atomWithStorage<DataFilter.Filter[]>(
    `${PREFIX}/${fileId}/treatment-definition`,
    []
  )
);

export const useTreatmentDefinition = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(treatmentDefinition(fileId));
};

export const useGetTreatmentDefinition = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(treatmentDefinition(fileId));
};

const treatmentTag = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/treatment-tag`, "treatment")
);

export const useTreatmentTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(treatmentTag(fileId));
};

export const useGetTreatmentTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(treatmentTag(fileId));
};

const controlTag = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/control-tag`, "control")
);

export const useControlTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(controlTag(fileId));
};

export const useGetControlTag = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(controlTag(fileId));
};

const timeColumn = atomFamily((fileId: string) =>
  atomWithStorage<string>(`${PREFIX}/${fileId}/time-column`, null)
);

export const useTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(timeColumn(fileId));
};

export const useGetTimeColumn = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(timeColumn(fileId));
};

const idVariables = atomFamily((fileId: string) =>
  atomWithStorage<string[]>(`${PREFIX}/${fileId}/id-columns`, [])
);

export const useIDVariables = () => {
  const fileId = useAtomValue(inputFile);
  return useAtom(idVariables(fileId));
};

export const useGetIDVariables = () => {
  const fileId = useAtomValue(inputFile);
  return useAtomValue(idVariables(fileId));
};
